import { create } from "zustand";
import { persist } from "zustand/middleware";
import { usersService } from "@/services/users";

export type TourName = "pipeline" | "flow" | "router";

export interface TourState {
  // Transient (not persisted)
  activeTour: TourName | null;
  currentStep: number;
  isRunning: boolean;
  helpers: Record<string, (...args: any[]) => any>;

  // Resume dialog state (transient)
  showResumeDialog: boolean;
  resumeTourName: TourName | null;
  resumeStepIndex: number;

  // Persisted
  completedTours: Record<string, boolean>;
  dontShowAgain: Record<string, boolean>;
  lastStepReached: Record<string, number>;

  // Actions
  startTour: (name: TourName, fromStep?: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;
  skipTour: () => void;
  completeTour: () => void;
  setDontShowAgain: (name: TourName, value: boolean) => void;
  registerHelper: (name: string, fn: (...args: any[]) => any) => void;
  unregisterHelper: (name: string) => void;
  clearHelpers: () => void;

  // Resume dialog actions
  offerResume: (name: TourName, step: number) => void;
  acceptResume: (fromSaved: boolean) => void;
  dismissResume: () => void;

  // Backend sync
  syncToBackend: () => void;
  initFromBackend: () => Promise<void>;
  resetAll: () => Promise<void>;
  resetTour: (name: TourName) => void;
}

let syncTimer: ReturnType<typeof setTimeout> | null = null;

function debouncedSync(state: TourState) {
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(() => {
    const data = {
      completedTours: state.completedTours,
      dontShowAgain: state.dontShowAgain,
      lastStepReached: state.lastStepReached,
    };
    usersService.updateTourState(data).catch(() => {});
  }, 3000);
}

export const useTourStore = create<TourState>()(
  persist(
    (set, get) => ({
      activeTour: null,
      currentStep: 0,
      isRunning: false,
      helpers: {},
      showResumeDialog: false,
      resumeTourName: null,
      resumeStepIndex: 0,
      completedTours: {},
      dontShowAgain: {},
      lastStepReached: {},

      startTour: (name, fromStep = 0) => {
        set({
          activeTour: name,
          currentStep: fromStep,
          isRunning: true,
          showResumeDialog: false,
        });
      },

      nextStep: () => {
        const { currentStep, activeTour } = get();
        if (!activeTour) return;
        const next = currentStep + 1;
        set({
          currentStep: next,
          lastStepReached: {
            ...get().lastStepReached,
            [activeTour]: next,
          },
        });
        debouncedSync(get());
      },

      prevStep: () => {
        const { currentStep } = get();
        if (currentStep > 0) {
          set({ currentStep: currentStep - 1 });
        }
      },

      goToStep: (step) => {
        const { activeTour } = get();
        if (!activeTour) return;
        set({
          currentStep: step,
          lastStepReached: {
            ...get().lastStepReached,
            [activeTour]: Math.max(get().lastStepReached[activeTour] || 0, step),
          },
        });
        debouncedSync(get());
      },

      skipTour: () => {
        const { activeTour, currentStep } = get();
        if (activeTour) {
          set({
            lastStepReached: {
              ...get().lastStepReached,
              [activeTour]: currentStep,
            },
          });
        }
        set({ activeTour: null, currentStep: 0, isRunning: false });
        debouncedSync(get());
      },

      completeTour: () => {
        const { activeTour } = get();
        if (activeTour) {
          set({
            completedTours: { ...get().completedTours, [activeTour]: true },
            lastStepReached: { ...get().lastStepReached, [activeTour]: 0 },
          });
        }
        set({ activeTour: null, currentStep: 0, isRunning: false });
        debouncedSync(get());
      },

      setDontShowAgain: (name, value) => {
        set({ dontShowAgain: { ...get().dontShowAgain, [name]: value } });
        debouncedSync(get());
      },

      registerHelper: (name, fn) => {
        set({ helpers: { ...get().helpers, [name]: fn } });
      },

      unregisterHelper: (name) => {
        const { [name]: _, ...rest } = get().helpers;
        set({ helpers: rest });
      },

      clearHelpers: () => {
        set({ helpers: {} });
      },

      offerResume: (name, step) => {
        set({ showResumeDialog: true, resumeTourName: name, resumeStepIndex: step });
      },

      acceptResume: (fromSaved) => {
        const { resumeTourName, resumeStepIndex } = get();
        if (resumeTourName) {
          set({ showResumeDialog: false });
          get().startTour(resumeTourName, fromSaved ? resumeStepIndex : 0);
        }
      },

      dismissResume: () => {
        set({ showResumeDialog: false, resumeTourName: null, resumeStepIndex: 0 });
      },

      syncToBackend: () => {
        debouncedSync(get());
      },

      initFromBackend: async () => {
        try {
          const data = await usersService.getTourState();
          const backend = data.tourState || {};
          const local = get();

          // Merge: most-progressed wins
          const mergedCompleted = { ...backend.completedTours, ...local.completedTours };
          const mergedDontShow = { ...backend.dontShowAgain, ...local.dontShowAgain };
          const mergedLastStep: Record<string, number> = { ...backend.lastStepReached };
          for (const [key, val] of Object.entries(local.lastStepReached)) {
            mergedLastStep[key] = Math.max(mergedLastStep[key] || 0, val);
          }

          set({
            completedTours: mergedCompleted,
            dontShowAgain: mergedDontShow,
            lastStepReached: mergedLastStep,
          });
        } catch {
          // Silently fail — localStorage is the fallback
        }
      },

      resetAll: async () => {
        try {
          await usersService.resetMyTours();
        } catch {
          // Continue with local reset even if backend fails
        }
        set({
          completedTours: {},
          dontShowAgain: {},
          lastStepReached: {},
          activeTour: null,
          currentStep: 0,
          isRunning: false,
        });
      },

      resetTour: (name) => {
        const { completedTours, dontShowAgain, lastStepReached } = get();
        const { [name]: _c, ...restCompleted } = completedTours;
        const { [name]: _d, ...restDontShow } = dontShowAgain;
        const { [name]: _l, ...restLastStep } = lastStepReached;
        set({
          completedTours: restCompleted,
          dontShowAgain: restDontShow,
          lastStepReached: restLastStep,
        });
        debouncedSync(get());
      },
    }),
    {
      name: "t2c-tour",
      partialize: (state) => ({
        completedTours: state.completedTours,
        dontShowAgain: state.dontShowAgain,
        lastStepReached: state.lastStepReached,
      }),
    },
  ),
);
